import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { Player } from '@lottiefiles/react-lottie-player';
import MaintenancePage from './ui/main/MaintenancePage';

// 점검 모드 설정
// - 환경 변수 VITE_MAINTENANCE_MODE=true 로 설정하거나
// - 아래 MAINTENANCE_MODE_OVERRIDE를 true로 변경하면 점검 페이지가 표시됩니다
const MAINTENANCE_MODE_OVERRIDE = false; // 긴급 점검 시 true로 변경
const MAINTENANCE_MODE = MAINTENANCE_MODE_OVERRIDE || import.meta.env.VITE_MAINTENANCE_MODE === 'true';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import package_json from "./../package.json";
import { IS_QA_ELIGIBLE } from './config/qaFeedback';

// itp-poc — 통역 통화 Phase 0 스파이크 (dev 전용). lazy: 운영 빌드에서 라우트가 접혀 청크 미로딩
const InterpretationPocPage = lazy(() => import('./ui/dev/itp-poc/InterpretationPocPage'));
// 통역 통화 게스트 링크 페이지 (Gate 3 스파이크, dev 전용) — 로그인 여부 무관 /call/:linkId
const GuestItpCallPage = lazy(() => import('./ui/dev/itp-call/GuestItpCallPage'));
import Page from './const/Page';
import { AppUIContext, AppModule } from './context/AppUIContext';
import { activateLanguage, defaultLanguage } from './locales/InitTranslation';
import { i18n } from '@lingui/core';
import { Message, SendFile } from './models/Message';
import { Room } from './models/Room';
import { RoomCall } from './models/RoomCall';
import { RoomItem } from './models/RoomItem';
import { Soribot } from './models/Soribot';
import { User } from './models/User';
import { Workspace } from './models/Workspace';
import { FilterType } from './models/local/Types';
import { Fire } from './services/FireClient';
import { My } from './services/My';
import { SuperAdmin } from './services/SuperAdmin';
import { twil } from './services/TwilioHelper';
import AppUrlListener from './services/Utils/AppUrlListener';
import { AndroidBackButton } from './services/Utils/AndroidBackButton';
import { appLifecycle } from './services/Utils/AppLifecycle';
import { PushService } from './services/Utils/PushService';
import { Util } from './services/Utils/Util';
import { MyNotifs } from './services/MyNotifs';
import { Badge as CapacitorBadge } from '@capawesome/capacitor-badge';
import CallHeader from './ui/components/CallHeader';
import CallInvitePopup from './ui/components/CallInvitePopup';
import EnvVersionBadge from './ui/components/EnvVersionBadge';
import LeftMenu from './ui/components/LeftMenu';
import { AppLayout } from './ui/components/AppLayout';
import ScreenRecordModal from './ui/components/ScreenRecordModal';
import AccountLanguagePage from './ui/main/AccountLanguage';
import AdminFlaggedPage from './ui/main/AdminFlaggedPage';

import ExternalPublicSharePage from './ui/main/ExternalPublicSharePage';
import FavoritesPage from './ui/main/FavoritesPage';
import LoginPage from './ui/main/LoginPage';
import MainPage, { GetAndroidPage, MainPageWelcome } from './ui/main/MainPage';
import MainPublicPage from './ui/main/MainPublicPage';
import RoomInvitationPage from './ui/main/RoomInvitationPage';
import SearchPage from './ui/main/SearchPage';
import TermsPage from './ui/main/TermsPage';
import UnavailablePage from './ui/main/UnavailablePage';
import MessageModal from './ui/rooms/MessageModal';
import RoomPage from './ui/rooms/RoomPage';
import RoomBotPage from './ui/rooms/right-menu/RoomBotPage';
import { TextAssistantModal } from './ui/rooms/right-menu/TextAssistantModal';
import MySoribotsPage from './ui/soribots/MySoribotsPage';
import PublicSoribotPage from './ui/soribots/PublicSoribotPage';
import SoribotSettingPage from './ui/soribots/SoribotSettingPage';


import WorkspaceInvitationPage from './ui/workspaces/WorkspaceInvitationPage';
import WorkspaceViewPage from './ui/workspaces/WorkspaceViewPage';
import AiMemoPage from './ui/ai-memo/AiMemoPage';
import FeedbackListPage from './ui/feedback/FeedbackListPage';
import BoardPage from './ui/board/BoardPage';
import CalendarPage from './ui/calendar/CalendarPage';
import FilesPlaceholder from './ui/files/FilesPlaceholder';

import { FeedbackGlobalTriggers } from './ui/feedback/FeedbackGlobalTriggers';
import { WhatsNewAutoTrigger } from './ui/components/WhatsNewModal';
import WSAdminBotsPage from './ui/workspaces/admin/WSAdminBotsPage';
import WSAdminRoomsPage from './ui/workspaces/admin/WSAdminRoomsPage';
import WSAdminSubscriptionPage from './ui/workspaces/admin/WSAdminSubscriptionPage';
import SuperAdminLayout from './ui/super-admin/SuperAdminLayout';
import SuperAdminDashboard from './ui/super-admin/SuperAdminDashboard';
import SuperAdminWorkspaces from './ui/super-admin/SuperAdminWorkspaces';
import SuperAdminWorkspaceDetail from './ui/super-admin/SuperAdminWorkspaceDetail';
import SuperAdminLoginPage from './ui/super-admin/SuperAdminLoginPage';
import SuperAdminTiersPage from './ui/super-admin/SuperAdminTiersPage';
import SuperAdminTierEditPage from './ui/super-admin/SuperAdminTierEditPage';
import SuperAdminPromotionsPage from './ui/super-admin/SuperAdminPromotionsPage';
import SuperAdminInquiriesPage from './ui/super-admin/SuperAdminInquiriesPage';
import SuperAdminInvoicesPage from './ui/super-admin/SuperAdminInvoicesPage';
import SuperAdminSubscriptionsPage from './ui/super-admin/SuperAdminSubscriptionsPage';
import SuperAdminSettingsPage from './ui/super-admin/SuperAdminSettingsPage';
import SuperAdminIssuerPage from './ui/super-admin/SuperAdminIssuerPage';
import SuperAdminSecurityPage from './ui/super-admin/SuperAdminSecurityPage';
import DevAdminLayout from './ui/dev-admin/DevAdminLayout';
import DevAdminDashboard from './ui/dev-admin/DevAdminDashboard';
import DevAdminCollections from './ui/dev-admin/DevAdminCollections';
import DevAdminCleanup from './ui/dev-admin/DevAdminCleanup';
import DevAdminWorkspaces from './ui/dev-admin/DevAdminWorkspaces';
import DevAdminStorage from './ui/dev-admin/DevAdminStorage';
import DevAdminRooms from './ui/dev-admin/DevAdminRooms';
import DevAdminUsers from './ui/dev-admin/DevAdminUsers';
import DevAdminScheduler from './ui/dev-admin/DevAdminScheduler';
import DevAdminFeedbacks from './ui/dev-admin/DevAdminFeedbacks';
import DevAdminItpCall from './ui/dev-admin/DevAdminItpCall';
import { MessageProvider } from "./ui/main/NotificationModal";
import { Toaster } from "./components/ui/toaster";
import { EnterType } from './ui/rooms/right-menu/RoomFilesModal';
import { InitialNavigationWrapper } from './hooks/useInitialNavigationWrapper';
import { WorkspacePermissionProvider } from './context/WorkspacePermissionContext';
import { WorkspaceSettingsProvider } from './context/WorkspaceSettingsContext';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
// @ts-ignore
import DevRoutes from '@dev-routes';
import NativeUpdateModal from './ui/components/NativeUpdateModal';
import { DevDarkModeToggle } from './ui/components/DevDarkModeToggle';
import { ThemeManager } from './ui/components/ThemeManager';
import { otaService } from './services/Utils/OtaUpdateService';

if (Capacitor.getPlatform() !== 'web') {
  StatusBar.setOverlaysWebView({ overlay: false });
  StatusBar.setStyle({ style: Style.Light });
  StatusBar.setBackgroundColor({ color: '#ffffff' });

  // SFSafariViewController(인앱 브라우저) 닫힐 때 StatusBar 복원
  // iOS가 WebView frame을 재계산한 후에 복원해야 하므로 딜레이 적용
  import('@capacitor/browser').then(({ Browser }) => {
    Browser.addListener('browserFinished', () => {
      // 즉시 1회 + iOS frame 재계산 후 1회
      StatusBar.setOverlaysWebView({ overlay: false });
      setTimeout(() => {
        StatusBar.setOverlaysWebView({ overlay: false });
        StatusBar.setStyle({ style: Style.Light });
        StatusBar.setBackgroundColor({ color: '#ffffff' });
      }, 300);
    });
  });
}

// Android용 간단한 토스트 함수
function showAndroidToast(message: string) {
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    bottom: 80px;
    left: 24px;
    right: 24px;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    padding: 14px 20px;
    border-radius: 12px;
    font-size: 14px;
    z-index: 99999;
    animation: fadeInOut 2s ease-in-out;
    text-align: center;
    line-height: 1.4;
  `;

  // 애니메이션 스타일 추가
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeInOut {
      0% { opacity: 0; transform: translateY(20px); }
      15% { opacity: 1; transform: translateY(0); }
      85% { opacity: 1; transform: translateY(0); }
      100% { opacity: 0; transform: translateY(-10px); }
    }
  `;
  document.head.appendChild(style);
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.remove();
    style.remove();
  }, 2000);
}

// Android 뒤로가기 버튼 핸들러 (두 번 누르면 종료)
if (Capacitor.getPlatform() === 'android') {
  let lastBackPress = 0;
  const BACK_PRESS_THRESHOLD = 2000; // 2초 내에 다시 누르면 종료

  CapApp.addListener('backButton', async () => {
    // 화면 내에서 닫을 것(태스크 상세 오버레이 등)이 등록돼 있으면 그것부터 닫는다
    if (AndroidBackButton.handleBack()) return;
    const now = Date.now();
    if (now - lastBackPress < BACK_PRESS_THRESHOLD) {
      await CapApp.exitApp();
    } else {
      lastBackPress = now;
      showAndroidToast(i18n._('Press back again to exit'));
    }
  });
}

var soribotPages: SoribotSettingPage[] = ['details', 'datasources', 'knowledgeviewer', 'freechat', 'testcases', 'testruns', 'suggestions', 'rooms', 'public', 'api', 'messages'];

const App: React.FC = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const a = queryParams.get('a');
  const [loginState, setLoginState] = useState<'progress' | 'authenticated' | 'loggedout'>('progress');
  const [connected, setConnected] = useState(false);
  const [filter, setFilter] = useState<FilterType>('none');
  const [iframeItem, setIframeItem] = useState<RoomItem | null>(null);
  const [messageToShow, setMessageToShow] = useState<Message | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [soribots, setSoribots] = useState<Soribot[]>([]);
  const [uploadFiles, setUploadFiles] = useState<SendFile[]>([]);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [enterType, setEnterType] = useState<EnterType>('link');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCalls, setActiveCalls] = useState(new Map<string, RoomCall>());
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [activeWorkspaceId, _setActiveWorkspaceId] = useState<string | null>(
    () => localStorage.getItem('activeWorkspaceId')
  );
  const setActiveWorkspaceId = useCallback((id: string | null) => {
    _setActiveWorkspaceId(id);
    if (id) {
      localStorage.setItem('activeWorkspaceId', id);
    } else {
      localStorage.removeItem('activeWorkspaceId');
    }
  }, []);

  // 워크스페이스 진입/전환·로그인 시 본인 멤버십을 Supabase workspace_members 미러에 self-heal.
  // boards/events 등 RLS 가 미러를 참조하므로 진입 시점에 정합성 보장 (서버 5분 캐시, 실패 무시).
  useEffect(() => {
    if (activeWorkspaceId && My.user?.id) {
      Fire.ws.ensureMemberSync(activeWorkspaceId);
    }
  }, [activeWorkspaceId, loginState]);

  const [activeModule, setActiveModule] = useState<AppModule>('chat');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    // Super Admin 상태 초기화 (sessionStorage에서 복원)
    if (SuperAdmin.isLoggedIn) {
      setIsSuperAdmin(true);
      setLoginState('authenticated');
    }

    Fire.auth.onLoginStateChanged = (user: User | null) => {
      if (user) {
        setLoginState('authenticated');
        // Sync Super Admin state from SuperAdmin class
        setIsSuperAdmin(SuperAdmin.isLoggedIn);
        Fire.getUserBlocks(user.id).then(list => {
          list.forEach(block => My.UserBlocks.set(block.blockedUser.id, block));
        })
        if (My.isLoggedIn()) {
          const userLang = My.user.lang || defaultLanguage;
          (window as any).__NEXTPOD_LANG__ = userLang;
          if (userLang !== defaultLanguage) {
            activateLanguage(userLang);
          }
        }
      }
      else {
        // Super Admin 로그인 상태면 loggedout으로 변경하지 않음
        if (!SuperAdmin.isLoggedIn) {
          setLoginState('loggedout');
        }
      }
    }

    window.addEventListener('appinstalled', async () => {
      Fire.auth.handleOnAuthStateChanged();
    });

    // OTA 번들 체크가 완료된 후에만 Auth 시작
    // → 번들 교체 중에 불필요한 앱 데이터 로딩 방지
    otaService.ready.then(() => {
      Fire.auth.handleOnAuthStateChanged();
    });

    // ⚠️ 안전 타임아웃: Auth 콜백이 15초 이내에 응답하지 않으면 로그인 페이지로 전환
    // 에뮬레이터 과부하, 네트워크 불안정 등으로 onAuthStateChanged가 발동하지 않는 케이스 방어
    const safetyTimer = setTimeout(() => {
      setLoginState(prev => {
        if (prev === 'progress') {
          console.warn('[App] Auth safety timeout (15s) — forcing loggedout state');
          return 'loggedout';
        }
        return prev;
      });
    }, 15000);

    const unsubTwilio = twil.subscribeToTwilioUpdates((e) => {
      if (e?.type == 'StateChange')
        setConnected(twil.isConnected())
    });
    return () => {
      clearTimeout(safetyTimer);
      unsubTwilio();
    };
  }, [])

  useEffect(() => {
    if (loginState == 'authenticated') {
      // 구독 설정 함수
      const setupSubscriptions = () => {
        // 알림 뱃지 음소거 필터(MyNotifs.isVisibleInCenter)는 My.RoomSettings 에 의존한다.
        // 알림 스냅샷이 도착하기 전에 로그인 유저의 roomSettings 로 맵을 먼저 채워
        // 첫 NrOfNew 계산부터 음소거가 반영되게 한다(스타트업 레이스 방지).
        My.updateRoomSettings();
        const unsub1 = Fire.ws.subscribeToWorkspaces(My.user.id, (wsList) => {
          setWorkspaces(wsList);
        });
        const unsub2 = Fire.bot.subscribeToMySoribots(My.user.id, (bots) => {
          setSoribots(bots.sort((a, b) => Util.dateSortFunc(a.created, b.created)));
        });
        const unsub3 = Fire.call.subscribeToRoomCalls(My.user.id, (calls) => {
          const map = new Map<string, RoomCall>();
          calls.forEach(c => map.set(c.id, c));
          setActiveCalls(map);
        });
        // 알림 구독 추가 (App 레벨에서 관리하여 왼쪽 메뉴가 닫혀있어도 업데이트됨)
        const unsub4 = Fire.subscribeToNewNotifications(My.user.id, (notifs) => {
          MyNotifs.setNotifications(notifs, My.RoomSettings);
          // ✅ OS 뱃지도 즉시 동기화 (MyNotifs.NrOfNew가 setNotifications에서 갱신된 후)
          if (Capacitor.isNativePlatform()) {
            CapacitorBadge.set({ count: MyNotifs.NrOfNew });
          }
        });

        // 채널 음소거(disablePushNotifications) 등 RoomSettings 가 로드/변경되면
        // 알림 뱃지를 다시 계산한다. setNotifications 가 NrOfNew(전역 벨)를 재계산하고
        // 'updated' 를 emit → 좌측 채널 목록(notifsByRoom)·알림 모달도 음소거 필터를 다시 적용.
        // (기존엔 알림 스냅샷이 올 때만 재계산돼, 음소거해도 채널 방문 전까지 오렌지 뱃지가 남았음)
        const unsub5 = My.subscribeToRoomSettingUpdates(() => {
          MyNotifs.setNotifications(MyNotifs.Notifications, My.RoomSettings);
          if (Capacitor.isNativePlatform()) {
            CapacitorBadge.set({ count: MyNotifs.NrOfNew });
          }
        });

        // AppLifecycle에 구독 등록 (백그라운드 시 자동 해제)
        appLifecycle.registerSubscription('workspaces', unsub1);
        appLifecycle.registerSubscription('soribots', unsub2);
        appLifecycle.registerSubscription('roomCalls', unsub3);
        appLifecycle.registerSubscription('notifications', unsub4);
        appLifecycle.registerSubscription('roomSettings', unsub5);
      };

      // 초기 구독 설정
      setupSubscriptions();

      // 포그라운드 복귀 시 재구독
      const unsubLifecycle = appLifecycle.onStateChange((isBackground) => {
        if (!isBackground && loginState === 'authenticated') {
          console.log('[App] Foreground detected, re-subscribing...');
          setupSubscriptions();
        }
      });

      return () => {
        appLifecycle.unsubscribeAll();
        unsubLifecycle();
      }
    }
  }, [loginState])

  return (
    <>
      {/* OTA: 네이티브 앱 강제/권고 업데이트 모달 */}
      <NativeUpdateModal />
      {/* 테마 관리 (시스템 다크모드 자동 감지) */}
      <ThemeManager isLoggedIn={loginState === 'authenticated'} />
      {/* 점검 모드일 때 점검 페이지만 표시 */}
      {MAINTENANCE_MODE ? (
        <MaintenancePage />
      ) : (
        <div className={`h-full w-full absolute inset-0 text-left ${connected ? 'connected' : ''}`}>
          {loginState == 'progress' &&
            <div className="flex flex-col items-center justify-center h-full w-full bg-background">
              <div id="just-a-moment" className="relative flex flex-col items-center">
                <Player autoplay keepLastFrame src="assets/splash.json" style={{ height: '160px', width: '160px' }}></Player>
                <p className='version absolute -bottom-8 text-xs text-muted-foreground'>v{package_json.version}</p>
              </div>
              <div className="w-64 h-1 bg-muted mt-8 rounded overflow-hidden">
                <div className="h-full bg-primary animate-progress"></div>
              </div>
            </div>
          }
          {loginState != 'progress' &&
            <>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AppUrlListener />
                <Toaster />
                <EnvVersionBadge />
                <CallInvitePopup />
                <CallHeader />
                <ScreenRecordModal />
                {loginState == 'authenticated' &&
                  <>
                    <TextAssistantModal />
                    <AppUIContext.Provider value={{
                      filter, setFilter, iframeItem, setIframeItem,
                      messageToShow, setMessageToShow, workspaces, setWorkspaces,
                      rooms, setRooms, soribots, setSoribots,
                      uploadFiles, setUploadFiles, showFilesModal, setShowFilesModal, enterType, setEnterType,
                      activeModule, setActiveModule,
                      isMobileMenuOpen, setIsMobileMenuOpen, activeCalls, setActiveCalls, roomsLoaded, setRoomsLoaded,
                      activeWorkspaceId, setActiveWorkspaceId
                    }}>
                      <InitialNavigationWrapper
                        onRoomsLoaded={(rooms) => setRooms(rooms)}
                        setRoomsLoaded={setRoomsLoaded}
                      />
                      <WorkspacePermissionProvider>
                        <WorkspaceSettingsProvider>
                        <MessageProvider>
                          <Routes>
                            {/* Dev Routes (ignored in git) */}
                            {DevRoutes.map((route: any) => (
                              <Route key={route.path} path={route.path} element={route.element} />
                            ))}
                            {/* Super Admin Routes - Independent layout, no AppLayout */}
                            {/* Check both React state and My.isSuperAdmin for immediate access after login */}
                            <Route path="/super-admin" element={(isSuperAdmin || SuperAdmin.isLoggedIn) ? <SuperAdminLayout /> : <Navigate to="/main" replace />}>
                              <Route index element={<SuperAdminDashboard />} />
                              <Route path="workspaces" element={<SuperAdminWorkspaces />} />
                              <Route path="workspaces/:workspaceId" element={<SuperAdminWorkspaceDetail />} />
                              <Route path="revenue/subscriptions" element={<SuperAdminSubscriptionsPage />} />
                              <Route path="revenue/invoices" element={<SuperAdminInvoicesPage />} />
                              <Route path="tiers" element={<SuperAdminTiersPage />} />
                              <Route path="tiers/:tierId" element={<SuperAdminTierEditPage />} />
                              <Route path="promotions" element={<SuperAdminPromotionsPage />} />
                              <Route path="inquiries" element={<SuperAdminInquiriesPage />} />
                              <Route path="settings" element={<SuperAdminSettingsPage />} />
                              <Route path="settings/issuer" element={<SuperAdminIssuerPage />} />
                              <Route path="settings/security" element={<SuperAdminSecurityPage />} />
                            </Route>
                            {/* Super Admin Login - Independent page, no AppLayout */}
                            <Route path="/super-login" element={<SuperAdminLoginPage />} />

                            {/* itp-poc — 통역 스파이크 (dev 전용, 운영 빌드 DCE. 설계 §15 Phase 0) */}
                            {IS_QA_ELIGIBLE && (
                              <Route path="/dev/itp-poc" element={
                                <Suspense fallback={null}><InterpretationPocPage /></Suspense>
                              } />
                            )}
                            {/* 통역 통화 링크 (dev 스파이크) — 로그인 상태에서도 전용 페이지로 (AppLayout 미경유) */}
                            {IS_QA_ELIGIBLE && (
                              <Route path="/call/:linkId" element={
                                <Suspense fallback={null}><GuestItpCallPage /></Suspense>
                              } />
                            )}

                            {/* Developer Admin Routes */}
                            <Route path="/dev-admin" element={<DevAdminLayout />}>
                              <Route index element={<DevAdminDashboard />} />
                              <Route path="collections" element={<DevAdminCollections />} />
                              <Route path="workspaces" element={<DevAdminWorkspaces />} />
                              <Route path="rooms" element={<DevAdminRooms />} />
                              <Route path="users" element={<DevAdminUsers />} />
                              <Route path="cleanup" element={<DevAdminCleanup />} />
                              <Route path="storage" element={<DevAdminStorage />} />
                              <Route path="scheduler" element={<DevAdminScheduler />} />
                              <Route path="feedbacks" element={<DevAdminFeedbacks />} />
                              <Route path="itp-call" element={<DevAdminItpCall />} />
                            </Route>
                            {/* Normal User Routes with AppLayout */}
                            <Route path="*" element={
                              <AppLayout>
                                <MessageModal />
                                <div id="main" className="ion-page h-full">
                                  <Routes>
                                    {/* Default redirect - always to /main, no auto admin redirect */}
                                    <Route path="/" element={<Navigate to="/main" replace />} />
                                    <Route path="/main" element={<MainPage />} />
                                    <Route path="/welcome" element={<MainPageWelcome />} />

                                    {/* Primary channel routes */}
                                    <Route path="/channel/:roomId" element={<RoomPage />} />
                                    <Route path="/channel/:roomId/bots" element={<RoomBotPage />} />
                                    <Route path="/channel/:roomId/favorites" element={<FavoritesPage />} />
                                    {/* Legacy /room/ routes kept for bookmarks & backwards compatibility */}
                                    <Route path="/room/:roomId" element={<RoomPage />} />
                                    <Route path="/room/:roomId/bots" element={<RoomBotPage />} />
                                    <Route path="/room/:roomId/favorites" element={<FavoritesPage />} />
                                    <Route path="/ws/:workspaceId" element={<WorkspaceViewPage />} />
                                    <Route path="/ws-admin/rooms/:workspaceId" element={<WSAdminRoomsPage />} />
                                    <Route path="/ws-admin/subscription/:workspaceId" element={<WSAdminSubscriptionPage />} />
                                    <Route path="/ws-admin/bots/:workspaceId" element={<WSAdminBotsPage />} />
                                    <Route path={`${Page.ai_memo}`} element={<AiMemoPage />} />
                                    <Route path={`${Page.feedback}`} element={<FeedbackListPage />} />
                                    <Route path={`${Page.board}/*`} element={<BoardPage />} />
                                    <Route path={`${Page.calendar}/*`} element={<CalendarPage />} />
                                    <Route path={`${Page.files}/*`} element={<FilesPlaceholder />} />

                                    <Route path="/login" element={<Navigate to="/main" replace />} />

                                    <Route path="/terms" element={<TermsPage />} />
                                    <Route path="/admin_flagged" element={<AdminFlaggedPage />} />
                                    <Route path="/invitation/:invitationId" element={<RoomInvitationPage />} />
                                    <Route path="/ws-invite/:invitationKey" element={<WorkspaceInvitationPage />} />
                                    <Route path="/get_android" element={<GetAndroidPage />} />
                                    <Route path="/search" element={<SearchPage />} />
                                    <Route path="/search/:roomId" element={<SearchPage />} />
                                    <Route path={`${Page.publicSharePage}/:linkId`} element={<ExternalPublicSharePage />} />
                                    <Route path={Page.my_soribots} element={<MySoribotsPage />} />
                                    {soribotPages.map(page => (
                                      <Route key={page} path={`${Page.soribot}/${page}/:botId`} element={<SoribotSettingPage page={page} />} />
                                    ))}
                                    <Route path={`${Page.publicBotPage}/:publicId`} element={<PublicSoribotPage />} />
                                    <Route path="*" element={<UnavailablePage />} />
                                  </Routes>
                                </div>
                              </AppLayout>
                            } />
                          </Routes>
                          <FeedbackGlobalTriggers />
                          <WhatsNewAutoTrigger />
                        </MessageProvider>
                        </WorkspaceSettingsProvider>
                      </WorkspacePermissionProvider>
                    </AppUIContext.Provider>
                  </>
                }
                {loginState == 'loggedout' && <>
                  <div id="main" className="h-full w-full bg-background">
                    <Routes>
                      {/* Super Admin Routes - 로그아웃 상태에서도 접근 가능 */}
                      {SuperAdmin.isLoggedIn && (
                        <Route path="/super-admin/*" element={<SuperAdminLayout />}>
                          <Route index element={<SuperAdminDashboard />} />
                          <Route path="workspaces" element={<SuperAdminWorkspaces />} />
                          <Route path="workspaces/:workspaceId" element={<SuperAdminWorkspaceDetail />} />
                          <Route path="subscriptions" element={<SuperAdminWorkspaces />} />
                          <Route path="documents" element={<SuperAdminDashboard />} />
                          <Route path="settings" element={<SuperAdminDashboard />} />
                        </Route>
                      )}
                      <Route path="/" element={<Navigate to="/login" replace />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/super-login" element={<SuperAdminLoginPage />} />
                      <Route path="/main" element={<Navigate to="/login" replace />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/invitation/:invitationId" element={<RoomInvitationPage />} />
                      <Route path="/ws-invite/:invitationKey" element={<WorkspaceInvitationPage />} />
                      <Route path={`${Page.publicSharePage}/:linkId`} element={<ExternalPublicSharePage />} />
                      <Route path={`${Page.publicBotPage}/:publicId`} element={<PublicSoribotPage />} />
                      {/* 통역 통화 링크 (dev 스파이크) — 비로그인 게스트 참여 (공개 라우트 선례: invitation/pub-share) */}
                      {IS_QA_ELIGIBLE && (
                        <Route path="/call/:linkId" element={
                          <Suspense fallback={null}><GuestItpCallPage /></Suspense>
                        } />
                      )}

                      <Route path="*" element={<Navigate to={SuperAdmin.isLoggedIn ? "/super-admin" : "/login"} replace />} />
                    </Routes>
                  </div>
                </>
                }
              </BrowserRouter>
            </>
          }
        </div>
      )}
    </>
  );
};

export default App;
